import { localizedStrings } from '@core/strings'
import React from 'react'
import { IconButton, Tooltip } from '@common/material'
import LaunchIcon from '@mui/icons-material/Launch'
import { LinkedProcessItemResult } from '@core/entities/linkedProcesses'
import { LinkedPeriodicCheckCard } from './LinkedPeriodicCheckCard'
import PeriodicCheckEntryModal from '@modules/permitDetails/modals/PeriodicCheckEntryModal'
import { useModal } from '@common/GlobalModal'
import {
  ChecklistCard,
  ObservationCard,
  PermitCard,
  RegisterItemCard,
  TaskCard,
} from '@elements'
import { LinkedObservationResult } from '@core/entities/linkedProcesses'
import { PermitDetailsModal } from '@modules/permitDetails/PermitDetailsModal'
import ChecklistDetailsModal from '@modules/permitDetails/modals/ChecklistDetailsModal'
import { TaskDetailsModal } from '@modules/tasks/taskDetails/modals/TaskDetailsModal'
import { RegisterItem } from '@core/entities/register/RegisterItem'
import { RegisterItemDetailsModal } from '@modules/registers/items'

type LinkedDocumentsListProps = {
  organisationId: number
  items: LinkedProcessItemResult[]
  permitId?: number
}

export const LinkedDocumentsList: React.FC<LinkedDocumentsListProps> = ({
  items,
  organisationId,
  permitId,
}) => {
  const { showModal } = useModal()

  return (
    <>
      {items?.map((item, index) => {
        if (['PERMIT', 'REFERENCED_BY_PERMIT'].includes(item.type)) {
          return (
            <PermitCard
              key={`${item.linkedPermitResult.id}-${index}`}
              templateName={item.linkedPermitResult.name}
              icon={
                <IconButton
                  color='primary'
                  size='small'
                  onClick={() => {
                    showModal(
                      <PermitDetailsModal
                        organisationId={organisationId}
                        permitId={item.linkedPermitResult.id}
                      />,
                    )
                  }}
                >
                  <Tooltip title={localizedStrings.viewPermit}>
                    <LaunchIcon fontSize='small' />
                  </Tooltip>
                </IconButton>
              }
              sx={{ mb: 2 }}
              {...item.linkedPermitResult}
            />
          )
        }

        if (['CHECKLIST', 'REFERENCED_BY_CHECKLIST'].includes(item.type)) {
          return (
            <ChecklistCard
              key={`${item.linkedChecklistResult.id}-${index}`}
              icon={
                <IconButton
                  color='primary'
                  size='small'
                  onClick={() => {
                    showModal(
                      <ChecklistDetailsModal
                        organisationId={organisationId}
                        checklistId={item.linkedChecklistResult.id}
                      />,
                    )
                  }}
                >
                  <Tooltip title={localizedStrings.viewChecklist}>
                    <LaunchIcon fontSize='small' />
                  </Tooltip>
                </IconButton>
              }
              sx={{ mb: 2 }}
              {...item.linkedChecklistResult}
            />
          )
        }

        if (['INSPECTION', 'REFERENCED_BY_INSPECTION'].includes(item.type)) {
          return (
            <ChecklistCard
              key={`${item.linkedInspectionResult.id}-${index}`}
              icon={
                <IconButton
                  color='primary'
                  size='small'
                  onClick={() => {
                    showModal(
                      <ChecklistDetailsModal
                        organisationId={organisationId}
                        checklistId={item.linkedInspectionResult.id}
                      />,
                    )
                  }}
                >
                  <Tooltip title={localizedStrings.viewInspection}>
                    <LaunchIcon fontSize='small' />
                  </Tooltip>
                </IconButton>
              }
              sx={{ mb: 2 }}
              {...item.linkedInspectionResult}
            />
          )
        }
        if (item.type === 'REFERENCED_BY_OBSERVATION') {
          return (
            <ObservationCard
              key={`${item.linkedObservationResult.id}-${index}`}
              observation={LinkedObservationResult.new(
                item.linkedObservationResult,
              )}
              sx={{ mb: 2 }}
            />
          )
        }

        if (item.type === 'TASK') {
          return (
            <TaskCard
              key={`${item.linkedTaskResult.id}-${index}`}
              task={item.linkedTaskResult}
              sx={{ mb: 2 }}
              icon={
                <IconButton
                  color='primary'
                  size='small'
                  data-test='view-task-button'
                  onClick={() => {
                    showModal(
                      <TaskDetailsModal taskId={item.linkedTaskResult.id} />,
                    )
                  }}
                >
                  <Tooltip title={localizedStrings.viewTask}>
                    <LaunchIcon fontSize='small' />
                  </Tooltip>
                </IconButton>
              }
            />
          )
        }

        if (item.type === 'PERIODIC_CHECK') {
          return (
            <LinkedPeriodicCheckCard
              key={`${item.linkedPeriodicChecksResult.id}-${index}`}
              linkedPeriodicChecksResult={item.linkedPeriodicChecksResult}
            >
              <IconButton
                color='primary'
                size='small'
                onClick={() => {
                  showModal(
                    <PeriodicCheckEntryModal
                      periodicCheckId={item.linkedPeriodicChecksResult.id}
                      periodicCheckTemplateId={
                        item.linkedPeriodicChecksResult.templatePeriodicCheckId
                      }
                      permitId={permitId}
                      organisationId={organisationId}
                    />,
                  )
                }}
              >
                <Tooltip title={localizedStrings.viewChecklist}>
                  <LaunchIcon fontSize='small' />
                </Tooltip>
              </IconButton>
            </LinkedPeriodicCheckCard>
          )
        }

        if (
          ['REGISTER_ITEM', 'REFERENCED_BY_REGISTER_ITEM'].includes(item.type)
        ) {
          return (
            <RegisterItemCard
              key={`${item.linkedRegisterItemResult.id}-${index}`}
              registerItem={RegisterItem.new(item.linkedRegisterItemResult)}
              previewPropertyIds={item.answer?.data[0]?.previewPropertyIds}
              sx={{ mb: 2 }}
              icon={
                <IconButton
                  color='primary'
                  size='small'
                  onClick={() => {
                    showModal(
                      <RegisterItemDetailsModal
                        registerId={item.linkedRegisterItemResult.register.id}
                        registerItemId={item.linkedRegisterItemResult.id}
                        scope={{
                          type: 'ORGANISATION',
                          organisationId,
                        }}
                      />,
                    )
                  }}
                >
                  <Tooltip title={localizedStrings.viewRegisterItem}>
                    <LaunchIcon fontSize='small' />
                  </Tooltip>
                </IconButton>
              }
            />
          )
        }
      })}
    </>
  )
}
